import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ColumnFilterPanel from '@/components/CustomizableTable/ColumnFilterPanel';
import { LOCATION_TYPES } from '@/constants/locationTypes';
import { MM_STATUSES, order_kind_options, order_types, tierOptions, wh_statuses } from './filterOptions';
import { MILESTONE_OPTIONS } from '@onward-delivery/core';
import ColumnFilterControls from '@/components/CustomizableTable/ColumnFilterControls';

export const COLUMN_FILTER_MAP = {
    shipper: {
        title: 'Shippers',
        controls: [{ type: 'multiAutocomplete', key: 'shipperIds', label: 'Shippers', source: 'shippers' }],
    },
    carrier: {
        title: 'Carriers',
        controls: [{ type: 'multiAutocomplete', key: 'carrierIds', label: 'Carriers', source: 'carriers' }],
    },
    assigned_admin: {
        title: 'Assigned Admin',
        controls: [
            {
                type: 'select',
                key: 'assignedAdminId',
                label: 'Assigned Admin',
                dynamicOptions: 'onward_admins',
            },
        ],
    },
    tier: {
        title: 'Tier',
        controls: [{ type: 'multiSelect', key: 'tiers', label: 'Tier', options: tierOptions }],
    },
    delivery_type: {
        title: 'Delivery Type',
        controls: [
            {
                type: 'multiSelect',
                key: 'deliveryTypes',
                label: 'Delivery Types',
                options: Object.entries(LOCATION_TYPES).map(([value, label]) => ({ value, label })),
            },
        ],
    },
    posted_date: {
        title: 'Order Created',
        controls: [{ type: 'dateRange', keys: ['startDate', 'endDate'], label: 'Created Between' }],
    },
    delivery_date: {
        title: 'Scheduled Delivery Date',
        controls: [{ type: 'dateRange', keys: ['schedDelStart', 'schedDelEnd'], label: 'Scheduled Between' }],
    },
    est_ship_date: {
        title: 'Estimated Shipment Date',
        controls: [{ type: 'dateRange', keys: ['estShipStart', 'estShipEnd'], label: 'Est. Ship Between' }],
    },
    est_received_date: {
        title: 'Estimated Warehouse Received Date',
        controls: [
            { type: 'dateRange', keys: ['warehouseStartDate', 'warehouseEndDate'], label: 'Est. Received Between' },
        ],
    },
    received_date: {
        title: 'Actual Received Date',
        controls: [
            { type: 'dateRange', keys: ['actualReceivedStart', 'actualReceivedEnd'], label: 'Received Between' },
        ],
    },
    warehousestatus: {
        title: 'Warehouse Status',
        controls: [{ type: 'select', key: 'warehouseStatus', label: 'Warehouse Status', options: wh_statuses }],
    },
    rereceivingstatus: {
        title: 'Rereceiving Status',
        controls: [{ type: 'select', key: 'rereceivingStatus', label: 'Rereceiving Status', options: wh_statuses }],
    },
    middle_mile_integration_status: {
        title: 'Middle Mile Status',
        controls: [{ type: 'select', key: 'middleMileStatus', label: 'Middle Mile Status', options: MM_STATUSES }],
    },
    order_type: {
        title: 'Order Type',
        controls: [{ type: 'select', key: 'orderType', label: 'Order Type', options: order_types }],
    },
    milestone: {
        title: 'Order Status',
        controls: [
            {
                type: 'select',
                key: 'milestone',
                label: 'Order Status',
                options: MILESTONE_OPTIONS.sort((a, b) => a.value.localeCompare(b.value)).map((o) => ({
                    value: o.value,
                    label: o.value,
                })),
            },
        ],
    },
    payment_type: {
        title: 'Payment Type',
        controls: [
            {
                type: 'select',
                key: 'paymentType',
                label: 'Payment Type',
                options: [
                    { value: '', label: 'All' },
                    { value: 'Credit Card', label: 'Credit Card' },
                    { value: 'Invoice', label: 'Invoice' },
                ],
            },
        ],
    },
    order_kind: {
        title: 'Order Kind',
        controls: [{ type: 'multiSelect', key: 'orderKinds', label: 'Order Kind', options: order_kind_options }],
    },
};

const AdminOrdersColumnFilterPanel = ({
    open,
    columnId,
    onClose,
    shippers = [],
    carriers = [],
    onwardAdminOptions = [],
    tab,
    shipperIds,
    setShipperIds,
    carrierIds,
    setCarrierIds,
    assignedAdminId,
    setAssignedAdminId,
    deliveryTypes,
    setDeliveryTypes,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    schedDelStart,
    setSchedDelStart,
    schedDelEnd,
    setSchedDelEnd,
    estShipStart,
    setEstShipStart,
    estShipEnd,
    setEstShipEnd,
    actualReceivedStart,
    setActualReceivedStart,
    actualReceivedEnd,
    setActualReceivedEnd,
    warehouseStartDate,
    setWarehouseStartDate,
    warehouseEndDate,
    setWarehouseEndDate,
    middleMileStatus,
    setMiddleMileStatus,
    warehouseStatus,
    setWarehouseStatus,
    rereceivingStatus,
    setRereceivingStatus,
    orderType,
    setOrderType,
    milestone,
    setMilestone,
    tiers,
    setTiers,
    paymentType,
    setPaymentType,
    orderKinds,
    setOrderKinds,
}) => {
    const config = COLUMN_FILTER_MAP[columnId];
    const [localFilters, setLocalFilters] = useState({});

    const shipperOptions = useMemo(() => {
        return shippers.map((shipper) => ({
            label: shipper.business_name,
            value: shipper.client_id,
        }));
    }, [shippers]);

    const carrierOptions = useMemo(
        () =>
            carriers.map((carrier) => ({
                label: carrier.business_name,
                value: carrier.client_id,
            })),
        [carriers]
    );

    useEffect(() => {
        if (!open || !config) return;

        const next = {};
        config.controls.forEach((control) => {
            if (control.type === 'dateRange') {
                control.keys.forEach((key) => {
                    next[key] =
                        {
                            startDate,
                            endDate,
                            schedDelStart,
                            schedDelEnd,
                            estShipStart,
                            estShipEnd,
                            actualReceivedStart,
                            actualReceivedEnd,
                            warehouseStartDate,
                            warehouseEndDate,
                        }[key] || '';
                });
            } else if (control.type === 'select') {
                next[control.key] =
                    {
                        middleMileStatus,
                        warehouseStatus,
                        rereceivingStatus,
                        orderType,
                        milestone,
                        paymentType,
                        assignedAdminId,
                    }[control.key] || '';
            } else if (control.type === 'multiAutocomplete') {
                next[control.key] = control.key === 'shipperIds' ? shipperIds || [] : carrierIds || [];
            } else if (control.type === 'multiSelect') {
                next[control.key] =
                    {
                        deliveryTypes: deliveryTypes || [],
                        tiers: tiers || [],
                        orderKinds: orderKinds || [],
                    }[control.key] || [];
            }
        });

        setLocalFilters(next);
    }, [
        open,
        config,
        startDate,
        endDate,
        schedDelStart,
        schedDelEnd,
        estShipStart,
        estShipEnd,
        actualReceivedStart,
        actualReceivedEnd,
        warehouseStartDate,
        warehouseEndDate,
        middleMileStatus,
        warehouseStatus,
        rereceivingStatus,
        orderType,
        milestone,
        paymentType,
        assignedAdminId,
        deliveryTypes,
        shipperIds,
        carrierIds,
        tiers,
        orderKinds,
    ]);

    const setValue = (key, value) => {
        setLocalFilters((prev) => ({ ...prev, [key]: value }));
    };

    const resolveOptions = useCallback(
        (control) => {
            if (control.type === 'multiAutocomplete') {
                return control.source === 'shippers' ? shipperOptions : carrierOptions;
            }
            if (control.type === 'select' && control.dynamicOptions === 'onward_admins') {
                return onwardAdminOptions;
            }
            return control.options || [];
        },
        [shipperOptions, carrierOptions, onwardAdminOptions]
    );

    if (!config) return null;

    const handleApply = () => {
        config.controls.forEach((control) => {
            if (control.type === 'dateRange') {
                control.keys.forEach((key) => {
                    const setter = {
                        startDate: setStartDate,
                        endDate: setEndDate,
                        schedDelStart: setSchedDelStart,
                        schedDelEnd: setSchedDelEnd,
                        estShipStart: setEstShipStart,
                        estShipEnd: setEstShipEnd,
                        actualReceivedStart: setActualReceivedStart,
                        actualReceivedEnd: setActualReceivedEnd,
                        warehouseStartDate: setWarehouseStartDate,
                        warehouseEndDate: setWarehouseEndDate,
                    }[key];
                    setter?.(localFilters[key] || null);
                });
            } else if (control.type === 'select') {
                const setter = {
                    middleMileStatus: setMiddleMileStatus,
                    warehouseStatus: setWarehouseStatus,
                    rereceivingStatus: setRereceivingStatus,
                    orderType: setOrderType,
                    milestone: setMilestone,
                    paymentType: setPaymentType,
                    assignedAdminId: setAssignedAdminId,
                }[control.key];
                setter?.(localFilters[control.key] || null);
            } else if (control.type === 'multiAutocomplete') {
                if (control.key === 'shipperIds') {
                    setShipperIds(localFilters[control.key] || []);
                } else if (control.key === 'carrierIds') {
                    setCarrierIds(localFilters[control.key] || []);
                }
            } else if (control.type === 'multiSelect') {
                if (control.key === 'deliveryTypes') {
                    setDeliveryTypes(localFilters[control.key] || []);
                } else if (control.key === 'tiers') {
                    setTiers(localFilters[control.key] || []);
                } else if (control.key === 'orderKinds') {
                    setOrderKinds(localFilters[control.key] || []);
                }
            }
        });
    };

    const handleClear = () => {
        config.controls.forEach((control) => {
            if (control.type === 'dateRange') {
                control.keys.forEach((key) => {
                    const setter = {
                        startDate: setStartDate,
                        endDate: setEndDate,
                        schedDelStart: setSchedDelStart,
                        schedDelEnd: setSchedDelEnd,
                        estShipStart: setEstShipStart,
                        estShipEnd: setEstShipEnd,
                        actualReceivedStart: setActualReceivedStart,
                        actualReceivedEnd: setActualReceivedEnd,
                        warehouseStartDate: setWarehouseStartDate,
                        warehouseEndDate: setWarehouseEndDate,
                    }[key];
                    setter?.(null);
                });
            } else if (control.type === 'select') {
                const setter = {
                    middleMileStatus: setMiddleMileStatus,
                    warehouseStatus: setWarehouseStatus,
                    rereceivingStatus: setRereceivingStatus,
                    orderType: setOrderType,
                    milestone: setMilestone,
                    paymentType: setPaymentType,
                    assignedAdminId: setAssignedAdminId,
                }[control.key];
                setter?.(null);
            } else if (control.type === 'multiAutocomplete') {
                if (control.key === 'shipperIds') {
                    setShipperIds([]);
                } else if (control.key === 'carrierIds') {
                    setCarrierIds([]);
                }
            } else if (control.type === 'multiSelect') {
                if (control.key === 'deliveryTypes') {
                    setDeliveryTypes([]);
                } else if (control.key === 'tiers') {
                    setTiers([]);
                } else if (control.key === 'orderKinds') {
                    setOrderKinds([]);
                }
            }
        });

        setLocalFilters({});
    };

    // Only show payment type filters on financials tab (tab index 10)
    if (columnId === 'payment_type' && tab !== 10) {
        return null;
    }

    return (
        <ColumnFilterPanel
            open={open}
            columnId={columnId}
            title={config.title}
            onClose={onClose}
            onApply={handleApply}
            onClear={handleClear}
            topOffset={50}
        >
            <ColumnFilterControls
                controls={config.controls}
                values={localFilters}
                setValue={setValue}
                resolveOptions={resolveOptions}
            />
        </ColumnFilterPanel>
    );
};

export default AdminOrdersColumnFilterPanel;
